import {
  Fragment,
  ChangeEvent,
  FC,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Close, Flex, Input, Link, SxStyleProp, Text } from 'theme-ui';
import { useTranslator } from '@picsart/rc/services/localization';
import Icon from 'shared/Icon';
import { VideoFileContainerEnum } from 'api';
import { getUsedTools } from 'modules/scenes';
import { getUser, isSubscribed } from 'modules/user';
import {
  buttonTitleStyle,
  labelStyle,
  linkStyle,
  subscribeMessageStyle
} from './ExportSettingsModalStyle';
import { Resolution } from 'types/Downloading';
import { getPremiumData, showCheckoutModal } from 'modules/checkoutModal';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import analytics from 'utils/analytics';
import { PaidFeatures } from 'types/AppState';
import { useAsync, useClickAway } from 'react-use';
import { DEFAULT_PROJECT_NAME } from 'constants/projects';
import { CombinedData, currencySymbol, PremiumData } from './OneTimePayAccordion';
import {
  PremiumItemSource,
  GROUPABLE_PREMIUM_ITEMS,
  RESOLUTIONS,
  TResolution
} from '../../constants';
import { getSubscriptionPackages } from 'utils/apiWrapper';
import {
  getOneTimePaymentStatus,
  getOneTimeRecentPaidItems,
  setOneTimePaymentReset
} from 'modules/oneTimePayment';
import { ExportAnalyticsData } from 'types/CheckoutModal';
import { download } from 'modules/downloading';

const FALLBACK_PACKAGE_ID = 'picsart';

const isGroupableType = (type: PaidFeatures) => GROUPABLE_PREMIUM_ITEMS.includes(type);

const isZeroPriced = (item: PremiumData) =>
  [PaidFeatures.Audio].includes(item.type) ||
  [PremiumItemSource.Unsplash].includes(item.source) ||
  ([PaidFeatures.VideoChooser].includes(item.type) &&
    [PremiumItemSource.Picsart].includes(item.source));

type Props = {
  isOpen: boolean;
  closeHandler: () => void;
  name: string;
};

const buttonIconStyle: SxStyleProp = { width: 14, height: 14, mr: '5px', ml: '-19px' };

export const ExportSettingsModal: FC<Props> = ({ isOpen, closeHandler, name = '' }) => {
  const [resolution, setResolution] = useState<Resolution>('360p');
  const [container, setContainer] = useState(VideoFileContainerEnum.Webm);
  const [namePrefix, changeNamePrefix] = useState(name);
  const dispatch = useDispatch();
  const isUserSubscribed = useSelector(isSubscribed);
  const usedTools = useSelector(getUsedTools);
  const translator = useTranslator();
  const [itemPackages, setItemPackages] = useState([]);
  const premiumItems = useSelector(getPremiumData);
  const paymentStatus = useSelector(getOneTimePaymentStatus);
  const recentlyPaidItems = useSelector(getOneTimeRecentPaidItems);
  const { isTrialUsed } = useSelector(getUser);
  const wrapper = useRef(null);
  const hyperTextLink = useMemo(
    () => (!isUserSubscribed && isTrialUsed ? 'Subscribe Now' : 'Start_your_free_trial.'),
    [isTrialUsed, isSubscribed]
  );

  const exportAnalyticsData: ExportAnalyticsData = {
    format: container,
    tool_names: usedTools,
    resolution
  };

  useClickAway(wrapper, () => {
    isOpen && closeHandler();
  });

  useAsync(async () => {
    const packagesData = await getSubscriptionPackages();
    setItemPackages(packagesData?.data);
  }, []);

  // enrich premiumItems with premium 1080p, 4k and Mp4
  const premiumItemsAll = useMemo(() => {
    const premiumItemsTemp = [...premiumItems];
    if (RESOLUTIONS.find((item: TResolution) => item.resolution === resolution)?.paid) {
      premiumItemsTemp.push({
        id: PaidFeatures.Resolution,
        type: PaidFeatures.Resolution,
        source: PremiumItemSource.Picsart
      });
    }

    if (container === VideoFileContainerEnum.Mp4) {
      premiumItemsTemp.push({
        id: PaidFeatures.Mp4,
        type: PaidFeatures.Mp4,
        source: PremiumItemSource.Picsart
      });
    }

    return premiumItemsTemp;
  }, [premiumItems, container, resolution]);

  const fallbackPackage = useMemo(() => itemPackages.find(({ id }) => id === FALLBACK_PACKAGE_ID), [
    itemPackages
  ]);

  // combinedData = premiumItemsAll, but with prices and count
  const combinedData = useMemo(
    () =>
      Object.values(
        premiumItemsAll.reduce<Record<string, CombinedData>>((acc, item) => {
          const currentPackage = itemPackages.find(
            ({ source, type }) => source === item.source && type === item.type
          );
          let price = currentPackage?.price.value ?? fallbackPackage?.price.value;

          // patch mainly for unsplash and music cases, because should be priced 0
          if (isZeroPriced(item)) {
            price = 0;
          }

          if (
            [PaidFeatures.PhotoChooser, PaidFeatures.VideoChooser].includes(item.type) &&
            [PremiumItemSource.Shutterstock].includes(item.source)
          ) {
            acc[item.id] = { ...item, count: 1, price };
            return acc;
          }

          const groupName = isGroupableType(item.type) ? item.type : item.id;
          const prevData = acc[groupName] || { ...item, count: 0, price };
          prevData.count++;
          acc[groupName] = prevData;
          return acc;
        }, {})
      ),
    [fallbackPackage, itemPackages, premiumItemsAll]
  );

  useEffect(() => {
    if (isOpen && itemPackages.length > 0) {
      analytics.exportPopupOneTimePayment(itemPackages[0].price.currency, totalAmount);
    }
  }, [isOpen]);

  useEffect(() => {
    if (premiumItemsAll.length && recentlyPaidItems.length && !wasAlreadyPaid) {
      dispatch(setOneTimePaymentReset());
    }
  }, [premiumItemsAll, recentlyPaidItems]);

  const { totalAmount } = useMemo(
    () =>
      combinedData.reduce(
        (acc, { count, price }) => ({
          totalCount: acc.totalCount + count,
          totalAmount: acc.totalAmount + count * price
        }),
        { totalCount: 0, totalAmount: 0 }
      ),
    [combinedData]
  );

  useEffect(() => {
    changeNamePrefix((name = DEFAULT_PROJECT_NAME ? translator(name) : name));
  }, [name]);

  useUpdateEffect(() => {
    if (isOpen) {
      analytics.exportPopupOpen({
        total_price: totalAmount,
        currency: currencySymbol?.toUpperCase(),
        is_subscribed: isUserSubscribed
      });
    } else {
      analytics.exportPopup({
        action_type: 'close',
        ...exportAnalyticsData
      });
    }
  }, [isOpen, totalAmount, isUserSubscribed, currencySymbol]);

  const onChangeNamePrefixHandler = (e: ChangeEvent<HTMLInputElement>) =>
    changeNamePrefix(
      e.target.value === DEFAULT_PROJECT_NAME ? translator(e.target.value) : e.target.value
    );

  const isPaid = premiumItemsAll.length > 0;
  const wasAlreadyPaid = useMemo(
    () =>
      paymentStatus && premiumItemsAll.every(item => recentlyPaidItems.find(i => i.id === item.id)),
    [premiumItemsAll, paymentStatus, recentlyPaidItems]
  );

  const onDownloadHandler = () => {
    const { width, height } = RESOLUTIONS.find(item => item.resolution === resolution);

    analytics.exportPopup({
      action_type: 'download',
      ...exportAnalyticsData
    });

    dispatch(download({ width, height, namePrefix, container, resolution }));

    // setTimeout — https://picsart.atlassian.net/browse/PW-5689
    setTimeout(() => closeHandler());
  };

  const onTrialDownloadHandler = useCallback(() => {
    analytics.exportPopup({
      action_type: 'download',
      ...exportAnalyticsData
    });

    dispatch(showCheckoutModal('download_button', premiumItems));
  }, []);

  return (
    <Box
      ref={wrapper}
      sx={{
        position: 'absolute',
        zIndex: 'popup',
        boxShadow: 'default',
        right: isOpen ? 0 : 10000
      }}
    >
      <Box
        bg="background"
        sx={{ borderRadius: 'modal', overflow: 'hidden', width: 414, position: 'relative', pt: 6 }}
        data-test="export-settings"
      >
        <Flex sx={{ justifyContent: 'flex-end', px: 4, py: 2 }}>
          <Close
            sx={{ position: 'absolute', top: '12px', right: '12px', outline: 'none' }}
            onClick={closeHandler}
            data-test="close"
          />
        </Flex>
        <Flex
          sx={{
            px: '32px',
            pb: 7,
            flexDirection: 'column'
          }}
        >
          <Box>
            <Text sx={labelStyle}>{translator('File_Name')}</Text>
            <Flex sx={{ alignItems: 'baseline', justifyContent: 'space', mt: 2 }}>
              <Input
                variant="text"
                value={namePrefix}
                onChange={onChangeNamePrefixHandler}
                data-test="export-settings_name"
              />
            </Flex>
          </Box>
          <Box sx={{ mt: 5 }}>
            <Text sx={labelStyle}>{translator('Quality')}</Text>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', mt: 2 }}>
            {RESOLUTIONS.map(item => (
              <Button
                variant={item.resolution === resolution ? 'selectActive' : 'select'}
                onClick={() => setResolution(item.resolution)}
                sx={{
                  mr: 2,
                  mb: 2,
                  px: 0,
                  py: 5,
                  justifyContent: 'center'
                }}
                key={item.resolution}
                data-test={`export-settings_resolution-button-${item.resolution}`}
              >
                {item.paid && <Icon name="gold-circle-gen" sx={buttonIconStyle} />}
                <Text sx={buttonTitleStyle}>{item.resolution}</Text>
              </Button>
            ))}
          </Box>
          <Box sx={{ mt: 5 }}>
            <Text sx={labelStyle}>{translator('File Type')}</Text>
          </Box>
          <Flex sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
            <Button
              variant={container === VideoFileContainerEnum.Webm ? 'selectActive' : 'select'}
              onClick={() => setContainer(VideoFileContainerEnum.Webm)}
              sx={{
                mr: 2,
                py: 5,
                justifyContent: 'center'
              }}
              data-test="export-settings_container-button-webm"
            >
              <Text sx={buttonTitleStyle}>{VideoFileContainerEnum.Webm}</Text>
            </Button>
            <Button
              variant={container === VideoFileContainerEnum.Mp4 ? 'selectActive' : 'select'}
              onClick={() => setContainer(VideoFileContainerEnum.Mp4)}
              sx={{
                mr: 2,
                py: 5,
                justifyContent: 'center'
              }}
              data-test="export-settings_container-button-mp4"
            >
              <Icon name="gold-circle-gen" sx={buttonIconStyle} />
              <Text sx={buttonTitleStyle}>{VideoFileContainerEnum.Mp4}</Text>
            </Button>
          </Flex>
          {!isUserSubscribed && isPaid && (
            <Fragment>
              <Flex sx={{ bg: 'grey2', p: 5, mt: 5, borderRadius: 'alert', alignItems: 'center' }}>
                <Box>
                  <Icon name="gold-circle-gen" />
                </Box>
                <Text sx={subscribeMessageStyle} as="p">
                  {translator(
                    'Don’t pay for premium items - unlock and download them with PicsArt Gold.'
                  )}{' '}
                  <Link
                    sx={linkStyle}
                    onClick={() => {
                      analytics.exportPopup({
                        action_type: 'unlock_gold',
                        ...exportAnalyticsData
                      });
                      dispatch(showCheckoutModal('download_message', premiumItems));
                    }}
                    data-test="downloaded-premium-text"
                  >
                    {translator(hyperTextLink)}
                  </Link>
                </Text>
              </Flex>
            </Fragment>
          )}
          {/* {!isUserSubscribed && isPaid && (
            <Fragment>
              <Flex sx={{ bg: 'grey2', p: 5, mt: 5, borderRadius: 'alert', alignItems: 'center' }}>
                <Box>
                  <Icon name="gold-circle-gen" />
                </Box>
                <Text sx={subscribeMessageStyle} as="p">
                  {translator('You’ve selected a premium feature. Unlock it and more with Gold.')}
                </Text>
              </Flex>
            </Fragment>
          )} */}
          <Box sx={{ mt: 5 }}>
            {!isUserSubscribed && isPaid && !wasAlreadyPaid && (
              <Button
                onClick={onTrialDownloadHandler}
                variant="primaryCentered"
                data-test="downloaded-button"
              >
                {translator('Start Free Trial')}
              </Button>
            )}
            {(isUserSubscribed || !isPaid || wasAlreadyPaid) && (
              <Button
                onClick={onDownloadHandler}
                variant="primaryCentered"
                data-test="downloaded-button"
              >
                {translator('Download')}
              </Button>
            )}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};
